import React, { useState, useEffect } from 'react';
import { Trash2, User, Shield, MoreHorizontal, Plus } from 'lucide-react';
import { db } from '../../../firebase';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import CreateUserModal from './CreateUserModal';
import '../AdminPanel.css';

const UsersSection = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const toggleMenu = (e, id) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX - 100 // Shift left to align
            });
            setOpenMenuId(id);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to remove this user from the list? (This does not delete the Auth account, only the database record)')) {
            try {
                await deleteDoc(doc(db, 'users', userId));
            } catch (error) {
                console.error("Error deleting user:", error);
                alert("Failed to delete user");
            }
        }
        setOpenMenuId(null);
    };

    useEffect(() => {
        const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const usersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                lastLogin: doc.data().lastLogin ? doc.data().lastLogin.toDate().toLocaleString() : 'Never'
            }));
            setUsers(usersData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching users:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">System</div>
                    <h1>Admin Users <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn btn-primary"
                    >
                        <Plus size={14} /> Invite User
                    </button>
                </div>
            </div>

            <div className="tbl-wrap">
                <div className="tbl-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Role</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="3" className="text-center p-4 text-gray-500">Loading users...</td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="text-center p-4 text-gray-500">No users found.</td>
                                </tr>
                            ) : (
                                users.map(u => (
                                    <tr key={u.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--surface3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <User size={12} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>{u.username || u.name || 'Unknown'}</div>
                                                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{u.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="badge badge-other"><Shield size={10} style={{ marginRight: '4px' }} />{u.role || 'User'}</span></td>
                                        <td style={{ position: 'relative' }}>
                                            <button
                                                onClick={(e) => toggleMenu(e, u.id)}
                                                className="btn-ghost"
                                                style={{ padding: '4px' }}
                                            >
                                                <MoreHorizontal size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Render Menu Outside Table using Fixed Position */}
                {openMenuId && (
                    <>
                        <div
                            style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                            onClick={() => setOpenMenuId(null)}
                        />
                        <div style={{
                            position: 'fixed',
                            top: `${menuPosition.top}px`,
                            left: `${menuPosition.left}px`,
                            background: 'white',
                            border: '1px solid #eee',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 100,
                            minWidth: '140px',
                            overflow: 'hidden'
                        }}>
                            <button
                                onClick={() => handleDeleteUser(openMenuId)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    width: '100%',
                                    padding: '10px 14px',
                                    border: 'none',
                                    background: 'white',
                                    color: '#ff4d4f',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    textAlign: 'left'
                                }}
                                className="hover:bg-red-50"
                            >
                                <Trash2 size={14} /> Remove User
                            </button>
                        </div>
                    </>
                )}
            </div>

            {showCreateModal && (
                <CreateUserModal
                    onClose={() => setShowCreateModal(false)}
                    onUserCreated={() => {
                        // Optional: Show success message or toast
                        console.log("User created successfully");
                    }}
                />
            )}
        </div>
    );
};

export default UsersSection;
